import React, { useMemo } from 'react';

import i18n from '@nextroll/ar-i18n';
import { useContainerContext } from '@nextroll/ar-container-context';
import { Placements, Popper, Tooltip } from '@nextroll/ar-style-base';

import { INodeIssue, ITriggerIssue, WORKFLOW_NODE_VALIDATION_ISSUE_CODES } from 'types/rofl';


interface IProps {
    triggerErrors?: ITriggerIssue[];
    triggerWarnings?: ITriggerIssue[];
    actionErrors?: INodeIssue[];
    actionWarnings?: INodeIssue[];
    onSelect?: () => void;
}

const WorkflowNameTooltip = ({
    type,
    triggerErrors = [],
    triggerWarnings = [],
    actionErrors = [],
    actionWarnings = [],
    onSelect,
}: { type: 'error' | 'warning' } & IProps) => {
    const { advertisable: { eid: advertisableEid } = {} } = useContainerContext();

    // determine if it contains the credit error which requires a special message
    const hasCreditError = !!actionErrors.find(
        error => error.error === WORKFLOW_NODE_VALIDATION_ISSUE_CODES.INSUFFICIENT_CONTACT_DISCOVERY_CREDITS
    );

    // total numbers of errors / warnings; it is used to display the tooltip content
    const numOfErrors = triggerErrors.length + actionErrors.length;
    const numOfWarnings = triggerWarnings.length + actionWarnings.length;

    /**
     * construct error message content, only when necessary
     */
    const errorMessage = useMemo(() => {
        if (type !== 'error') {
            return ''; // this is an warning tooltip; do not render the error message
        }
        if (triggerErrors.length > 0 && actionErrors.length > 0) {
            if (triggerErrors.length > 1 && actionErrors.length > 1) {
                return i18n.gettext('{{ numOfTriggerErrors }} errors found in Trigger and {{ numOfActionErrors }} errors found in Action.', {
                    numOfTriggerErrors: triggerErrors.length,
                    numOfActionErrors: actionErrors.length,
                });
            }
            if (triggerErrors.length > 1) {
                return i18n.gettext('{{ numOfTriggerErrors }} errors found in Trigger and 1 error found in Action.', { numOfTriggerErrors: triggerErrors.length });
            }
            if (actionErrors.length > 1) {
                return i18n.gettext('1 error found in Trigger and {{ numOfActionErrors }} errors found in Action.', { numOfActionErrors: actionErrors.length });
            }
            return i18n.gettext('1 error found in Trigger and 1 error found in Action.');
        }
        if (triggerErrors.length > 0) {
            return i18n.ngettext(
                '{{ numOfTriggerErrors }} error found in Trigger.',
                '{{ numOfTriggerErrors }} errors found in Trigger.',
                triggerErrors.length,
                { numOfTriggerErrors: triggerErrors.length });
        }
        if (actionErrors.length > 0) {
            return i18n.ngettext(
                '{{ numOfActionErrors }} error found in Action.',
                '{{ numOfActionErrors }} errors found in Action.',
                actionErrors.length,
                { numOfActionErrors: actionErrors.length });
        }
        return '';
    }, [type, triggerErrors.length, actionErrors.length]);

    /**
     * construct warning message content, only when necessary
     */
    const warningMessage = useMemo(() => {
        if (type !== 'warning') {
            return ''; // this is an error tooltip; do not render the warning message
        }
        if (triggerWarnings.length > 0 && actionWarnings.length > 0) {
            if (triggerWarnings.length > 1 && actionWarnings.length > 1) {
                return i18n.gettext('{{ numOfTriggerWarnings }} warnings found in Trigger and {{ numOfActionWarnings }} warnings found in Action.', {
                    numOfTriggerWarnings: triggerWarnings.length,
                    numOfActionWarnings: actionWarnings.length,
                });
            }
            if (triggerWarnings.length > 1) {
                return i18n.gettext('{{ numOfTriggerWarnings }} warnings found in Trigger and 1 warning found in Action.', { numOfTriggerWarnings: triggerWarnings.length });
            }
            if (actionWarnings.length > 1) {
                return i18n.gettext('1 warning found in Trigger and {{ numOfActionWarnings }} warnings found in Action.', { numOfActionWarnings: actionWarnings.length });
            }
            return i18n.gettext('1 warning found in Trigger and 1 warning found in Action.');
        }
        if (triggerWarnings.length > 0) {
            return i18n.ngettext(
                '{{ numOfTriggerWarnings }} warning found in Trigger.',
                '{{ numOfTriggerWarnings }} warnings found in Trigger.',
                triggerWarnings.length,
                { numOfTriggerWarnings: triggerWarnings.length });
        }
        if (actionWarnings.length > 0) {
            return i18n.ngettext(
                '{{ numOfActionWarnings }} warning found in Action.',
                '{{ numOfActionWarnings }} warnings found in Action.',
                actionWarnings.length,
                { numOfActionWarnings: actionWarnings.length });
        }
        return '';
    }, [type, triggerWarnings.length, actionWarnings.length]);

    const messageBlocks = useMemo(() => [
        type === 'error' && numOfErrors > Number(hasCreditError) && ( // for trigger and action errors but not the credit usage error
            <span key='workflows-table-name-tooltip-content-error' data-testid='workflows-table-name-tooltip-error'>
                <div className='mb-1'>{errorMessage}</div>
                <a role='button' href='#' onClick={onSelect}>
                    <i className='fa fa-pen mr-1' />
                    {i18n.gettext('Edit Workflow')}
                </a>
            </span>
        ),
        type === 'warning' && numOfWarnings > 0 && ( // for trigger and action warnings
            <span key='workflows-table-name-tooltip-content-warning' data-testid='workflows-table-name-tooltip-warning'>
                <div className='mb-1'>{warningMessage}</div>
                <a role='button' href='#' onClick={onSelect}>
                    <i className='fa fa-pen mr-1' />
                    {i18n.gettext('Edit Workflow')}
                </a>
            </span>
        ),
        type === 'error' && hasCreditError && ( // for the credit usage error
            <span key='workflows-table-name-tooltip-content-credit-usage' data-testid='workflows-table-name-tooltip-credit-usage'>
                <div className='mb-1'>
                    {i18n.gettext('Your Contact Credits have run out. You can purchase more credits by contacting your Account Manager.')}
                </div>
                <a role='button' href={`/settings/subscription-usage?advertisable=${advertisableEid}`}>
                    <i className='fa fa-external-link-alt mr-1' />
                    {i18n.gettext('View Plan & Usage')}
                </a>
            </span>
        ),
    ].filter(Boolean), [
        type, numOfErrors, errorMessage, numOfWarnings, warningMessage, hasCreditError, advertisableEid, onSelect,
    ]);

    if (type === 'error' && numOfErrors <= 0) {
        return null; // no errors; do NOT render the error label + tooltip
    }
    if (type === 'warning' && numOfWarnings <= 0) {
        return null; // no warnings; do NOT render the warning label + tooltip
    }

    const tooltipContent = (
        <Tooltip className='workflow-table-workflow-name-tooltip' placement={Placements.RIGHT}>
            {messageBlocks}
        </Tooltip>
    );

    return (
        <Popper hoverContent={tooltipContent} className='mr-2'>
            {/* popper requires a "block" container to render the tooltip placement correctly */}
            <div>
                <i className={`fa fa-sm fa-exclamation-${type === 'error' ? 'circle text-danger' : 'triangle text-warning'} mr-1`} />
                <span className={`small text-${type === 'error' ? 'danger' : 'warning'}`}>
                    {type === 'error'
                        ? i18n.ngettext('{{ numOfErrors }} Error', '{{ numOfErrors }} Errors', numOfErrors, { numOfErrors })
                        : i18n.ngettext('{{ numOfWarnings }} Warning', '{{ numOfWarnings }} Warnings', numOfWarnings, { numOfWarnings })}
                </span>
            </div>
        </Popper>
    );
};

/**
 * Element for displaying formatted workflow name, including the error and warning messages.
 */
export const WorkflowTableNameCell = (props: { name: string } & IProps) => (
    <span data-testid={props.name}>
        <strong className='workflow-list-name'>{props.name}</strong>
        <br />
        <WorkflowNameTooltip type='error' {...props} />
        <WorkflowNameTooltip type='warning' {...props} />
    </span>
);
